import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Receipt } from "lucide-react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";
import { DeleteAccountSection } from "@/components/DeleteAccountSection";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "—");

export default function Account() {
  const { user } = useAuth();
  const paywall = usePaywall();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/payments/history").then(({ data }) => setData(data)).catch(() => setData({ purchases: [] }));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="account-page">
      <Header />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-14">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2">Account</div>
        <h1 className="text-4xl sm:text-5xl tracking-tighter font-black mb-2">{user?.name}</h1>
        <p className="text-sm text-[#52525B] mb-10" data-testid="account-email">{user?.email}</p>

        <div className="border border-black/10 p-6 mb-10 flex flex-wrap items-center justify-between gap-4" data-testid="account-access">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#ED5A24] text-white flex items-center justify-center"><Lock className="w-5 h-5" /></div>
            <div>
              <div className="font-bold">{user?.has_access ? "Admin — all logos unlocked" : `Pay per logo · ${paywall.price}`}</div>
              <div className="text-sm text-[#52525B]">Previews are free. Unlock any logo from your dashboard to download all its files.</div>
            </div>
          </div>
          <Link to="/history" data-testid="account-my-logos-btn"
            className="bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-11 px-6 inline-flex items-center transition-colors duration-200">
            My logos
          </Link>
        </div>

        <h2 className="text-lg font-bold tracking-tight mb-4">Purchases & receipts</h2>
        {!data ? (
          <p className="text-sm text-[#52525B]">Loading…</p>
        ) : data.purchases.length === 0 ? (
          <p className="text-sm text-[#52525B]" data-testid="account-no-purchases">No purchases yet.</p>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10" data-testid="account-purchases">
            {data.purchases.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`purchase-${i}`}>
                <div>
                  <div className="font-medium">{p.description}</div>
                  <div className="text-xs text-[#52525B]">{fmtDate(p.date)}</div>
                </div>
                <div className="flex items-center gap-5">
                  <span className="font-bold">{p.currency === "AUD" ? "A$" : ""}{p.amount.toFixed(2)}{p.currency === "AUD" ? "" : ` ${p.currency}`}</span>
                  {p.receipt_url && (
                    <a href={p.receipt_url} target="_blank" rel="noreferrer" data-testid={`purchase-receipt-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-bold text-[#ED5A24] hover:underline">
                      <Receipt className="w-3.5 h-3.5" /> Receipt
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {user?.role !== "admin" && <DeleteAccountSection />}
      </div>
    </div>
  );
}
